import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { LanguageApiRoaming } from '@api-roaming/models/language-api-roaming';
import { ApiRoamingRoamingService } from '@api-roaming/services/api-roaming-roaming.service';
import { ErrorResponse } from '@api/models';
import { LOCALIZATION_CONFIG, LocalizationConfig } from '@core/localization/localization.config';
import { StartWithUnit } from '@core/operators/start-with-unit';
import { LocalStorage } from '@core/storage/local-storage';
import { StorageKeys } from '@core/storage/storage.keys';
import { TranslateService } from '@ngx-translate/core';
import { RxState } from '@rx-angular/state';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';


export interface ILanguageState {
    language: string;
    loading: Record<StartWithUnit, boolean>;
    errors: Record<StartWithUnit, ErrorResponse>;
    languages: LanguageApiRoaming[];
}

export interface ILanguageAction {
    requestLanguages: LanguageApiRoaming[];
    setCurrentLanguage: string;
}

@Injectable({
    providedIn: 'root',
})
export class LanguageState extends RxState<ILanguageState> {
    @LocalStorage(StorageKeys.SystemLocale)
    public languageCodeFromStorage: string;

    readonly #document: Document = inject(DOCUMENT);
    readonly #apiRoamingRoamingService: ApiRoamingRoamingService = inject(ApiRoamingRoamingService);
    readonly #translate: TranslateService = inject(TranslateService);
    readonly #actions: RxActions<ILanguageAction> = new RxActionFactory<ILanguageAction>().create();
    readonly #dateAdapter: DateAdapter<unknown> = inject(DateAdapter);
    readonly #localizationConfig: LocalizationConfig = inject(LOCALIZATION_CONFIG);

    readonly #defaultLanguageCode: string = this.#localizationConfig.defaultLocale;

    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');
    public readonly errors$: Observable<Record<StartWithUnit, ErrorResponse>> = this.select('errors');
    public readonly language$: Observable<string> = this.select('language');
    public readonly languages$: Observable<LanguageApiRoaming[]> = this.select('languages');


    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();

        this.#translate.setFallbackLang(this.#defaultLanguageCode);
        this.#translate.setDefaultLang(this.#defaultLanguageCode);
        this.setCurrentLanguage(this.languageCodeFromStorage || this.#defaultLanguageCode);
    }

    public setCurrentLanguage(languageCode: string): void {
        const isoCode: string = languageCode?.toLowerCase();

        if (isoCode === this.get('language')) {
            return;
        }

        this.#translate.use(isoCode);
        this.#dateAdapter.setLocale(this.#localizationConfig.defaultDateLocal);
        this.#document.documentElement.lang = isoCode;
        this.#actions.setCurrentLanguage(isoCode);
    }

    public requestLanguages(): void {
        this.hold(
            this.#apiRoamingRoamingService.apiRoamingListlanguagesGet(),
            this.#actions.requestLanguages,
        );
    }


    private setDefaultState(): void {
        this.set({
            loading: null,
            errors: null,
            languages: null,
        });
    }

    private connectSelectors(): void {
        this.connect('language', this.#actions.setCurrentLanguage$.pipe(
            tap((language: string) => this.languageCodeFromStorage = language.toLowerCase()),
        ));

        this.connect('languages', this.#actions.requestLanguages$.pipe(
            tap((languages: LanguageApiRoaming[]) => {
                const currentLanguage: string = this.languageCodeFromStorage;

                if (languages.length > 0 && currentLanguage &&
                    !languages?.find((language: LanguageApiRoaming) => language.code?.toLowerCase() === currentLanguage)) {
                    this.languageCodeFromStorage = languages[0]?.code?.toLowerCase();
                    window.location.reload();
                }
            }),
        ));
    }
}
